import { useCallback, useEffect, useRef, useState } from 'react';
import {
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type EdgeChange,
  type NodeChange,
} from '@xyflow/react';
import type { ProcessGraphDto } from '@api/processes';
import {
  dtoToReactFlow,
  makeId,
  resolveAccent,
  type Port,
  type ProcessEdge,
  type ProcessNode,
  type ProcessNodeData,
} from './graphTypes';
import { useGraphHistory, type GraphSnapshot } from './history/useGraphHistory';

/**
 * Stable structural fingerprint of a graph snapshot. Picks ONLY the fields
 * that round-trip through the wire DTO (id / type / position / data for
 * nodes; id / source / sourceHandle / target / targetHandle / type / data
 * for edges) so React Flow's runtime flags (`selected`, `dragging`,
 * `measured`, ...) don't flip the dirty bit on what is, structurally, the
 * same graph. JSON.stringify is fine here -- the keys are projected in a
 * fixed order so the output is deterministic across calls.
 */
const snapshotFingerprint = (snap: GraphSnapshot): string =>
  JSON.stringify({
    nodes: snap.nodes.map((n) => ({
      id: n.id,
      type: n.type,
      position: n.position,
      data: n.data,
    })),
    edges: snap.edges.map((e) => ({
      id: e.id,
      source: e.source,
      sourceHandle: e.sourceHandle,
      target: e.target,
      targetHandle: e.targetHandle,
      type: e.type,
      data: e.data,
    })),
  });

const areSnapshotsEqual = (a: GraphSnapshot, b: GraphSnapshot): boolean =>
  snapshotFingerprint(a) === snapshotFingerprint(b);

/**
 * Build an offset, fully independent copy of {@code source} for the
 * "duplicate selection" toolbar action. The copy shares no mutable
 * sub-structure with the original:
 *
 * <ul>
 *   <li>Node {@code id} is freshly minted.</li>
 *   <li>{@code data.ports} (if present) is deep-cloned and every
 *       {@code portId} is replaced with a fresh UUID — sharing port
 *       ids across nodes makes the backend's
 *       {@code EdgePortIntegrityRule} fail with
 *       {@code EDGE_PORT_NODE_MISMATCH} the first time a wire is drawn
 *       from the duplicate.</li>
 *   <li>Task nodes' {@code outputInstructions} (keyed by old port id)
 *       are re-keyed to the new port ids so the per-output instruction
 *       text follows the matching port.</li>
 *   <li>The persisted run snapshot ({@code latestExecution},
 *       {@code latestArtifacts}, {@code isRunnable}) is cleared so the
 *       duplicate doesn't inherit the original's "completed" badge.</li>
 *   <li>{@code data} itself is shallow-cloned (covers Note nodes, which
 *       have no ports / run snapshot) so a later edit on the duplicate
 *       can't mutate the original's data bag.</li>
 * </ul>
 */
function cloneNodeForDuplicate(source: ProcessNode): ProcessNode {
  const newPosition = {
    x: source.position.x + 40,
    y: source.position.y + 40,
  };
  const data = source.data as Record<string, unknown>;
  const rawPorts = (data as { ports?: unknown }).ports;
  if (!Array.isArray(rawPorts)) {
    return {
      ...source,
      id: makeId(),
      position: newPosition,
      selected: false,
      data: { ...data } as ProcessNodeData,
    } as ProcessNode;
  }

  const portIdMap = new Map<string, string>();
  const clonedPorts: Port[] = (rawPorts as Port[]).map((p) => {
    const next = makeId();
    portIdMap.set(p.portId, next);
    return { ...p, portId: next };
  });

  const clonedData: Record<string, unknown> = {
    ...data,
    ports: clonedPorts,
    latestExecution: null,
    latestArtifacts: [],
    isRunnable: false,
  };

  const rawOutputInstructions = (data as { outputInstructions?: unknown })
    .outputInstructions;
  if (
    rawOutputInstructions !== null &&
    typeof rawOutputInstructions === 'object'
  ) {
    const remapped: Record<string, string> = {};
    for (const [portId, instruction] of Object.entries(
      rawOutputInstructions as Record<string, string>,
    )) {
      const next = portIdMap.get(portId);
      if (next) remapped[next] = instruction;
    }
    clonedData.outputInstructions = remapped;
  }

  return {
    ...source,
    id: makeId(),
    position: newPosition,
    selected: false,
    data: clonedData as ProcessNodeData,
  } as ProcessNode;
}

interface UseProcessGraphReturn {
  nodes: ProcessNode[];
  edges: ProcessEdge[];
  /** React Flow change handler — wired to `<ReactFlow onNodesChange>` */
  onNodesChange: (changes: NodeChange<ProcessNode>[]) => void;
  /** React Flow change handler — wired to `<ReactFlow onEdgesChange>` */
  onEdgesChange: (changes: EdgeChange<ProcessEdge>[]) => void;
  /** Add a freshly-built node (e.g. after a palette drop). */
  addNode: (node: ProcessNode) => void;
  /** Patch a node's `data`. Other node fields are left untouched. */
  updateNodeData: (id: string, patch: Partial<ProcessNodeData>) => void;
  /** Remove a node and any edges that touch it. */
  removeNode: (id: string) => void;
  /**
   * `true` when the graph has changed since it was last seeded (`initial`)
   * or marked clean (after a successful save). Drives the Save button's
   * enabled state and the unsaved-changes warning banner.
   *
   * Selection-only changes (clicking nodes / edges) do NOT flip this flag —
   * we filter them out at the change-applier so opening a node in the
   * inspector doesn't accidentally trigger "unsaved changes".
   */
  isDirty: boolean;
  /**
   * Re-snapshot the current graph as the new "saved" state. Call this after
   * a successful `PUT /processes/:id`.
   */
  markClean: () => void;
  /**
   * Programmatically select a single node (deselecting any others). Used by
   * the validation panel to scroll to + highlight the offending node when an
   * error row is clicked. Pass `null` to clear all selection.
   */
  selectNode: (id: string | null) => void;
  /**
   * Programmatically select a single edge (deselecting any others). Used by
   * the validation panel for edge-anchored errors (port type mismatch /
   * port-id integrity). Pass `null` to clear all selection.
   */
  selectEdge: (id: string | null) => void;
  /**
   * Connect two ports. Only fires when both `sourceHandle` and `targetHandle`
   * are set (port UUIDs) — drags without explicit handles silently no-op
   * because the wire format requires UUIDs on every edge.
   * Dedupes against existing edges with the same
   * (source, sourceHandle, target, targetHandle) tuple — drag-completing the
   * same wire twice silently no-ops rather than producing duplicates.
   */
  connect: (connection: Connection) => void;
  /** Replace the whole graph (e.g. after a fresh fetch). */
  setGraph: (graph: ProcessGraphDto) => void;

  // ── Multi-id mutators (canvas toolbar Edit group) ────────────────────────
  /**
   * Remove every supplied node id (and any edges that touch one of them)
   * in a single transaction. Records exactly one history frame so undo
   * restores the whole batch in one click. Unknown ids are silently
   * dropped so the caller can hand the current React-Flow selection
   * over verbatim without filtering it first.
   */
  removeNodes: (ids: readonly string[]) => void;
  /**
   * Remove every supplied edge id in a single transaction. Records
   * exactly one history frame so undo restores the whole batch. Unknown
   * ids are silently dropped — callers can hand the current React-Flow
   * selection over verbatim without filtering.
   */
  removeEdges: (ids: readonly string[]) => void;
  /**
   * Duplicate every supplied node id at a fixed `+40, +40` offset. Edges
   * are NOT cloned -- the duplicates start as unwired siblings so the
   * user can re-route them deliberately. Pushes one history frame for
   * the whole batch.
   */
  duplicateNodes: (ids: readonly string[]) => void;
  /**
   * Paste a batch of node snapshots back onto the canvas. Each snapshot
   * is re-cloned (fresh ids + port ids, run snapshot cleared) so a
   * single clipboard payload can be pasted multiple times without
   * collisions. Edges are NOT recreated — pasted nodes start as unwired
   * siblings, mirroring {@link duplicateNodes}.
   *
   * <p>When {@code target} is provided, the cloned subgraph's bounding
   * box is translated so its top-left lands at {@code target} (canvas
   * world coordinates). Otherwise the cloned nodes keep their original
   * relative layout, offset by the same `+40, +40` step that
   * {@code duplicateNodes} uses so the pasted copy doesn't overlap the
   * source.</p>
   */
  pasteNodes: (
    snapshots: readonly ProcessNode[],
    target?: { x: number; y: number },
  ) => void;
  /**
   * Replace every node's `position` with the value taken from the same id
   * in `next`. Used by Auto-arrange. Records one history frame.
   * Caller may pass a sparse array -- ids missing from `next` keep their
   * existing position.
   */
  applyLayout: (next: readonly ProcessNode[]) => void;

  // ── Undo / redo ──────────────────────────────────────────────────────────
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  undo: () => void;
  redo: () => void;
}

/**
 * State container for the editor's React Flow canvas. Wraps the standard
 * React Flow change-applier with a couple of editor-specific niceties:
 *
 *   - Always carries `dragHandle: '.node-card__drag-handle'` so React Flow
 *     only listens for drags from the card header (body fields stay
 *     editable without grabbing the whole node).
 *   - Edge ids are UUIDs (per plan §13.2) and edges carry a `data.accent`
 *     stamped at translate-time so the BranchedEdge renderer doesn't have
 *     to look up the source gate's port at every render.
 *   - Connect dedupes — re-completing the same drag is a no-op.
 *
 * Designed to be the only writable view of canvas state in the editor;
 * everything outside the canvas (validation, save, test run) reads from
 * `nodes` + `edges` and never mutates them directly.
 */
export function useProcessGraph(
  initial: ProcessGraphDto | undefined,
  processId?: string | null,
): UseProcessGraphReturn {
  // We rebuild nodes + edges synchronously whenever the consumer hands us a
  // different `initial` reference (e.g. React Query landed a fresh
  // `useProcessDetail` payload, or the user switched processes). Doing the
  // reset during render — via the `useState` + previous-value sentinel
  // pattern from the React docs — avoids the cascading-render warning we'd
  // get from putting a `setNodes` call inside a `useEffect`.
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [seedRef, setSeedRef] = useState<ProcessGraphDto | undefined>(initial);
  // Track the processId tied to the current seed so we can detect a
  // route/process switch independently of the reference identity of
  // `initial` (which is `undefined` while the new query is in flight).
  const [seedProcessId, setSeedProcessId] = useState<string | null | undefined>(
    processId,
  );
  const [nodes, setNodes] = useState<ProcessNode[]>(() =>
    initial ? dtoToReactFlow(initial).nodes : [],
  );
  const [edges, setEdges] = useState<ProcessEdge[]>(() =>
    initial ? dtoToReactFlow(initial).edges : [],
  );
  const [isDirty, setDirty] = useState<boolean>(false);

  // The "clean" baseline -- whatever shape the canvas had at the last seed
  // / save / programmatic setGraph. Undo and redo compare the restored
  // snapshot against this to decide whether the user has now landed back
  // on the saved state (in which case `isDirty` should flip back to false
  // even though we just applied a state change).
  const cleanSnapshotRef = useRef<GraphSnapshot>(
    initial ? dtoToReactFlow(initial) : { nodes: [], edges: [] },
  );

  // Connect needs to read the latest `nodes` snapshot to compute a new
  // edge's accent (the source port's label on a Gate decides approved /
  // rejected tinting). A ref keeps the connect callback identity-stable
  // while still giving it a fresh read of the current node set. The ref
  // is synced via useEffect (per the React refs rule — refs must not be
  // mutated during render).
  const nodesRef = useRef<ProcessNode[]>(nodes);
  const edgesRef = useRef<ProcessEdge[]>(edges);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);
  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  // Undo/redo machinery. `pushSnapshot()` is called immediately before
  // every mutating change so undo restores the *pre-mutation* state.
  // The history hook dedupes nothing; it's the caller's job to avoid
  // pushing redundant frames (e.g. an `onNodesChange` that only carries
  // selection events shouldn't produce a frame).
  const history = useGraphHistory();
  const pushSnapshot = useCallback(() => {
    history.push({ nodes: nodesRef.current, edges: edgesRef.current });
  }, [history]);

  // A processId change always wins over the dirty guard: if the user
  // navigated to a different process, any pending edits on the previous
  // canvas no longer apply, so we discard them. For same-process refetches
  // (window focus, manual refetch) we keep the dirty guard so a
  // background reseed doesn't clobber in-progress edits.
  const processIdChanged = processId !== seedProcessId;
  const sameProcessReseed = initial !== seedRef && !isDirty;
  if (processIdChanged || sameProcessReseed) {
    setSeedRef(initial);
    setSeedProcessId(processId);
    const next = initial ? dtoToReactFlow(initial) : { nodes: [], edges: [] };
    setNodes(next.nodes);
    setEdges(next.edges);
    setDirty(false);
    history.reset();
  }

  // Mirror seedRef into the clean-snapshot ref AFTER render commits.
  // Mutating refs during render is a lint trap (and rightly so -- it
  // tears between renders); doing it in an effect keeps the baseline
  // in sync without that hazard. setGraph + markClean update the ref
  // directly from their event-handler bodies, so this effect only
  // covers the prop-driven reseed path.
  useEffect(() => {
    cleanSnapshotRef.current = seedRef
      ? dtoToReactFlow(seedRef)
      : { nodes: [], edges: [] };
  }, [seedRef]);

  // ── Dirty tracking ────────────────────────────────────────────────────────
  // React Flow surfaces selection / dimension / position-while-dragging as
  // change events. We only want to flip "dirty" for changes that actually
  // mutate the persisted shape — selection toggles and viewport-only events
  // shouldn't dirty the editor.
  const isMutatingNodeChange = (c: NodeChange<ProcessNode>): boolean => {
    if (c.type === 'select') return false;
    if (c.type === 'dimensions') return false;
    if (c.type === 'position') {
      // React Flow fires intermediate position events with `dragging: true`
      // continuously during a drag and a final one with `dragging: false`
      // when the drag ends. Only the final commit dirties the graph;
      // intermediate events get filtered so we don't flag the canvas dirty
      // simply for clicking and not committing a drag.
      return c.dragging === false;
    }
    return true;
  };
  const isMutatingEdgeChange = (c: EdgeChange<ProcessEdge>): boolean =>
    c.type !== 'select';

  const onNodesChange = useCallback(
    (changes: NodeChange<ProcessNode>[]) => {
      const mutating = changes.some(isMutatingNodeChange);
      if (mutating) pushSnapshot();
      setNodes((curr) => applyNodeChanges(changes, curr));
      if (mutating) setDirty(true);
    },
    [pushSnapshot],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange<ProcessEdge>[]) => {
      const mutating = changes.some(isMutatingEdgeChange);
      if (mutating) pushSnapshot();
      setEdges((curr) => applyEdgeChanges(changes, curr));
      if (mutating) setDirty(true);
    },
    [pushSnapshot],
  );

  const addNode = useCallback(
    (node: ProcessNode) => {
      pushSnapshot();
      setNodes((curr) => [...curr, node]);
      setDirty(true);
    },
    [pushSnapshot],
  );

  const updateNodeData = useCallback(
    (id: string, patch: Partial<ProcessNodeData>) => {
      pushSnapshot();
      // A ports patch can delete ports; edges are wired to port UUIDs
      // (sourceHandle / targetHandle), so a removed port leaves dangling
      // wires the backend rejects on save with "Edge references unknown
      // node or port". Diff against the node's current ports and drop the
      // orphaned edges in the same batch as the node patch.
      const nextPorts = (patch as { ports?: Port[] }).ports;
      let removedPortIds: Set<string> | null = null;
      if (Array.isArray(nextPorts)) {
        const prevNode = nodesRef.current.find((n) => n.id === id);
        const prevPorts = (prevNode?.data as { ports?: Port[] } | undefined)
          ?.ports;
        if (Array.isArray(prevPorts)) {
          const keptIds = new Set(nextPorts.map((p) => p.portId));
          const removed = prevPorts
            .filter((p) => !keptIds.has(p.portId))
            .map((p) => p.portId);
          if (removed.length > 0) removedPortIds = new Set(removed);
        }
      }
      setNodes((curr) =>
        curr.map((n) =>
          n.id === id
            ? // Preserve the discriminant — React Flow's typing requires the
              // overall data shape to stay assignable to ProcessNodeData. The
              // intersection cast is safe because callers only patch keys that
              // belong to the current node type.
              ({ ...n, data: { ...n.data, ...patch } as ProcessNodeData })
            : n,
        ),
      );
      if (removedPortIds) {
        const dropped = removedPortIds;
        setEdges((curr) =>
          curr.filter(
            (e) =>
              !(
                (e.source === id && dropped.has(e.sourceHandle ?? '')) ||
                (e.target === id && dropped.has(e.targetHandle ?? ''))
              ),
          ),
        );
      }
      setDirty(true);
    },
    [pushSnapshot],
  );

  const removeNode = useCallback(
    (id: string) => {
      pushSnapshot();
      setNodes((curr) => curr.filter((n) => n.id !== id));
      setEdges((curr) =>
        curr.filter((e) => e.source !== id && e.target !== id),
      );
      setDirty(true);
    },
    [pushSnapshot],
  );

  // Multi-id remove. Drops everything in one transaction so the editor
  // never renders an intermediate state with the nodes gone but their
  // dangling edges still painted (a single setState batch keeps both
  // arrays consistent across the same React commit).
  const removeNodes = useCallback(
    (ids: readonly string[]) => {
      if (ids.length === 0) return;
      const idSet = new Set(ids);
      pushSnapshot();
      setNodes((curr) => curr.filter((n) => !idSet.has(n.id)));
      setEdges((curr) =>
        curr.filter((e) => !idSet.has(e.source) && !idSet.has(e.target)),
      );
      setDirty(true);
    },
    [pushSnapshot],
  );

  // Edge-only remove. Mirrors removeNodes' one-history-frame semantics
  // so the toolbar's Delete button can drop a wire selection cleanly —
  // the Del key already worked via React Flow's default onEdgesChange,
  // but the toolbar action was nodes-only.
  const removeEdges = useCallback(
    (ids: readonly string[]) => {
      if (ids.length === 0) return;
      const idSet = new Set(ids);
      pushSnapshot();
      setEdges((curr) => curr.filter((e) => !idSet.has(e.id)));
      setDirty(true);
    },
    [pushSnapshot],
  );

  // Duplicate selected nodes -- positions offset diagonally so the
  // copies don't sit on top of the originals. Wires are intentionally
  // dropped: the duplicates are independent siblings the user can
  // route deliberately, not a structural copy of the subgraph.
  const duplicateNodes = useCallback(
    (ids: readonly string[]) => {
      if (ids.length === 0) return;
      const idSet = new Set(ids);
      pushSnapshot();
      setNodes((curr) => {
        const clones: ProcessNode[] = curr
          .filter((n) => idSet.has(n.id))
          .map((n) => ({ ...cloneNodeForDuplicate(n), selected: true }));
        const deselected = curr.map((n) =>
          idSet.has(n.id) ? { ...n, selected: false } : n,
        );
        return [...deselected, ...clones];
      });
      setDirty(true);
    },
    [pushSnapshot],
  );

  // Paste a clipboard payload. Re-clones via `cloneNodeForDuplicate`
  // (which already adds the +40/+40 offset) so a paste with no target
  // looks like a duplicate. With a target, we translate the cloned
  // subgraph so its bounding-box top-left lands at the requested
  // canvas-world coordinates -- preserves the relative layout the
  // user copied. Mirrors `duplicateNodes` for selection / edge
  // semantics: the pasted nodes are selected, all others are
  // deselected, and no edges are reconstructed.
  const pasteNodes = useCallback(
    (
      snapshots: readonly ProcessNode[],
      target?: { x: number; y: number },
    ) => {
      if (snapshots.length === 0) return;
      pushSnapshot();
      const cloned = snapshots.map((n) => ({
        ...cloneNodeForDuplicate(n),
        selected: true,
      }));
      // When a target is supplied, shift the whole batch so its
      // bounding-box top-left lands at `target`. The cloned nodes
      // already carry the +40 offset from `cloneNodeForDuplicate`;
      // we compute the translation from THEIR (already-offset) bbox
      // so the math stays consistent regardless of the source layout.
      let placed = cloned;
      if (target) {
        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        for (const n of cloned) {
          if (n.position.x < minX) minX = n.position.x;
          if (n.position.y < minY) minY = n.position.y;
        }
        const dx = target.x - minX;
        const dy = target.y - minY;
        placed = cloned.map((n) => ({
          ...n,
          position: { x: n.position.x + dx, y: n.position.y + dy },
        }));
      }
      setNodes((curr) => {
        const deselected = curr.some((n) => n.selected)
          ? curr.map((n) => ({ ...n, selected: false }))
          : curr;
        return [...deselected, ...placed];
      });
      setDirty(true);
    },
    [pushSnapshot],
  );

  // Replace each node's `position` from the supplied array. Used by
  // Auto-arrange. Passing a sparse list is allowed -- ids missing
  // from `next` keep their existing position.
  const applyLayout = useCallback(
    (next: readonly ProcessNode[]) => {
      if (next.length === 0) return;
      const positions = new Map(next.map((n) => [n.id, n.position]));
      pushSnapshot();
      setNodes((curr) =>
        curr.map((n) => {
          const pos = positions.get(n.id);
          return pos ? { ...n, position: pos } : n;
        }),
      );
      setDirty(true);
    },
    [pushSnapshot],
  );

  const undo = useCallback(() => {
    const restored = history.undo({
      nodes: nodesRef.current,
      edges: edgesRef.current,
    });
    if (!restored) return;
    setNodes([...restored.nodes]);
    setEdges([...restored.edges]);
    // Derive dirty from the restored shape: undoing back to the saved
    // baseline must clear the unsaved-changes indicator, otherwise the
    // user can never get out of the dirty state without another save.
    setDirty(!areSnapshotsEqual(restored, cleanSnapshotRef.current));
  }, [history]);

  const redo = useCallback(() => {
    const restored = history.redo({
      nodes: nodesRef.current,
      edges: edgesRef.current,
    });
    if (!restored) return;
    setNodes([...restored.nodes]);
    setEdges([...restored.edges]);
    // Same logic as undo -- redo can also land on the clean baseline if
    // the user undid past it AFTER a markClean (the saved state can sit
    // in the middle of the history stack, not just at the bottom).
    setDirty(!areSnapshotsEqual(restored, cleanSnapshotRef.current));
  }, [history]);

  const connect = useCallback(
    (connection: Connection) => {
      const { source, target, sourceHandle, targetHandle } = connection;
      // Wire format requires port UUIDs on both ends — drags without explicit
      // handles can't be persisted, so silently drop them.
      if (!source || !target || !sourceHandle || !targetHandle) return;
      // Snapshot BEFORE applying so undo restores the pre-edge state.
      // We snapshot up front and then bail out below if the wire was a
      // duplicate -- a no-op snapshot is harmless (one extra "do nothing"
      // undo step) but mis-ordering it past the dedupe check would lose
      // the snapshot for a wire that *did* land.
      const exists = edgesRef.current.some(
        (e) =>
          e.source === source &&
          e.target === target &&
          e.sourceHandle === sourceHandle &&
          e.targetHandle === targetHandle,
      );
      if (exists) return;
      pushSnapshot();
      setEdges((curr) => {
        const accent = resolveAccent(nodesRef.current, source, sourceHandle);
        const newEdge: ProcessEdge = {
          id: makeId(),
          source,
          sourceHandle,
          target,
          targetHandle,
          type: 'branched',
          data: { accent },
        };
        // Calling `setDirty` from inside another state-updater is allowed
        // (React batches them) and is more reliable than a closed-over `let
        // added` flag — under StrictMode the updater runs twice and the
        // outer flag would be inspected before the second run lands.
        setDirty(true);
        return [...curr, newEdge];
      });
    },
    [pushSnapshot],
  );

  const setGraph = useCallback(
    (graph: ProcessGraphDto) => {
      const next = dtoToReactFlow(graph);
      setNodes(next.nodes);
      setEdges(next.edges);
      cleanSnapshotRef.current = next;
      setDirty(false);
      history.reset();
    },
    [history],
  );

  const markClean = useCallback(() => {
    cleanSnapshotRef.current = {
      nodes: nodesRef.current,
      edges: edgesRef.current,
    };
    setDirty(false);
  }, []);

  const selectNode = useCallback((id: string | null) => {
    setNodes((curr) =>
      curr.map((n) => ({ ...n, selected: id !== null && n.id === id })),
    );
    setEdges((curr) =>
      // Clearing edge selection when focusing a node mirrors React Flow's
      // own click-to-select behaviour — a single thing is "active" at a time.
      curr.some((e) => e.selected)
        ? curr.map((e) => ({ ...e, selected: false }))
        : curr,
    );
  }, []);

  const selectEdge = useCallback((id: string | null) => {
    setEdges((curr) =>
      curr.map((e) => ({ ...e, selected: id !== null && e.id === id })),
    );
    setNodes((curr) =>
      curr.some((n) => n.selected)
        ? curr.map((n) => ({ ...n, selected: false }))
        : curr,
    );
  }, []);

  return {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    addNode,
    updateNodeData,
    removeNode,
    removeNodes,
    removeEdges,
    duplicateNodes,
    pasteNodes,
    applyLayout,
    selectNode,
    selectEdge,
    connect,
    setGraph,
    isDirty,
    markClean,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    undo,
    redo,
  };
}
